/* eslint-disable prettier/prettier */
import React, { ChangeEvent, useEffect, useState } from 'react';

import nexo from '../../nexoClient';

import {
  Page,
  Layout,
  FormField,
  EmptyMessage,
  InteractiveList,
} from '@nimbus-ds/patterns';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Icon,
  IconButton,
  Input,
  Pagination,
  Sidebar,
  Spinner,
  Table,
  Tabs,
  Text,
  Thumbnail,
  Title,
  Link,
  useToast,
} from '@nimbus-ds/components';
import { navigateHeader } from '@tiendanube/nexo/helpers';
import {
  IStoreConfig,
  defaultStoreConfig,
} from '../../lib/interfaces/config.interfaces';
import apiInstance from '../../utils/apiUtils';
import { InputColor } from '../../components';
import {
  ForbiddenIcon,
  PlusCircleIcon,
  SearchIcon,
  TrashIcon,
  UndoIcon,
} from '@nimbus-ds/icons';
import { IPaginateMeta } from '../../lib/interfaces/paginate.interfaces';

const ConfigsPage: React.FC = () => {
  const { addToast } = useToast();
  const [loadingGetConfig, setLoadingGetConfig] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [config, setConfig] = useState<IStoreConfig | null>(null);
  const [defaultLang, setDefaultLang] = useState<'pt' | 'es' | 'en'>('pt');
  const [loadingProducts, setLoadingProducts] = useState<boolean>(true);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [openSearchProdut, setOpenSearchProdut] = useState<boolean>(false);
  const [openSearchCategory, setOpenSearchCategory] = useState<boolean>(false);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [productsMeta, setProductsMeta] = useState<IPaginateMeta>();
  const [categoryMeta, setCategoryMeta] = useState<IPaginateMeta>();
  const [searchProductQuery, setSearchProductQuery] = useState<string>('');

  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  useEffect(() => {
    navigateHeader(nexo, { goTo: '/', text: 'Voltar' });
    getConfig();
    setLoading(false);
  }, []);

  const handleChangeProductQuerySearch = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setSearchProductQuery(value);

    // Limpa o timeout anterior, se existir
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Define um novo timeout de 500 milissegundos (ou o valor desejado)
    const newTimeout = setTimeout(() => {
      searchProducts(productsMeta?.page || 1);
    }, 500);

    setSearchTimeout(newTimeout);

    return false;
  };

  const searchProducts = async (page = 1, reset = false) => {
    setLoadingProducts(true);
    try {
      const result = await apiInstance.get(
        `/store/products?page=${page}${
          reset || searchProductQuery === '' ? '' : `&q=${searchProductQuery}`
        }`,
      );
      if (result.data !== null) {
        setProducts(result.data.result);
        setProductsMeta(result.data.meta as IPaginateMeta);
        setDefaultLang(result.data.meta.lang || 'pt');
      }
    } catch (error) {
      setProducts([]);
    }
    setLoadingProducts(false);
  };

  const searchCategories = async (page = 1) => {
    setLoadingCategories(true);
    try {
      const result = await apiInstance.get(`/store/categories?page=${page}`);
      if (result.data !== null) {
        const uniqueData: any[] = [];
        const ids = new Set();

        result.data.result.forEach((item: any) => {
          if (!ids.has(item.id)) {
            uniqueData.push(item);
            ids.add(item.id);
          }
        });

        setCategories(uniqueData);
        setCategoryMeta(result.data.meta as IPaginateMeta);
      }
    } catch (error) {
      setCategories([]);
    }
    setLoadingCategories(false);
  };

  const handleRemoveProductForBlackList = (id: number) => {
    if (!config) {
      return false;
    }
    if (config.black_list_products && id in config.black_list_products) {
      delete config.black_list_products[id];
    }
    setConfig({ ...config });
  };

  const handleRemoveCategoryForBlackList = (id: number) => {
    if (!config) {
      return false;
    }
    if (config.black_list_category && id in config.black_list_category) {
      delete config.black_list_category[id];
    }
    setConfig({ ...config });
  };

  const handleAddCategoryToBlackList = async (
    event: ChangeEvent<HTMLInputElement>,
    category: any,
  ) => {
    const { checked } = event.target;
    if (!config) {
      return false;
    }
    if (checked) {
      if (!config.black_list_category) {
        config.black_list_category = {};
      }
      config.black_list_category[category.id] = category.name[defaultLang];
    } else {
      if (
        config.black_list_category &&
        category.id in config.black_list_category
      ) {
        delete config.black_list_category[category.id];
      }
    }
    setConfig({ ...config });
  };

  const handleAddProductToBlackList = async (
    event: ChangeEvent<HTMLInputElement>,
    product: any,
  ) => {
    const { checked } = event.target;
    if (!config) {
      return false;
    }
    if (checked) {
      if (!config.black_list_products) {
        config.black_list_products = {};
      }
      config.black_list_products[product.id] = product.name[defaultLang];
    } else {
      if (
        config.black_list_products &&
        product.id in config.black_list_products
      ) {
        delete config.black_list_products[product.id];
      }
    }
    setConfig({ ...config });
  };

  const handleOpenSidebarProduct = () => {
    searchProducts();
    setOpenSearchProdut(true);
  };

  const handleOpenSidebarCategory = () => {
    searchCategories();
    setOpenSearchCategory(true);
  };

  const handleChangeConfig = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;

    const helpConfig: any = { ...config };
    helpConfig[name] =
      type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    setConfig({ ...helpConfig });
    return false;
  };

  const getConfig = async () => {
    try {
      const result = await apiInstance.get(`/store/config`);
      const tempConfig = { ...(result.data as IStoreConfig) };

      setConfig({ ...tempConfig });
    } catch (error) {
      setConfig({ ...defaultStoreConfig });
    }
    setLoadingGetConfig(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const data = JSON.parse(JSON.stringify(config));
      delete data._id;
      delete data.createdAt;
      delete data.updatedAt;
      delete data.store_id;
      await apiInstance.post(`/store/config`, data);
      addToast({
        id: 'submit' + new Date().toISOString(),
        type: 'success',
        text: `Configuração salva com sucesso`,
      });
      setLoading(false);
    } catch (error) {
      addToast({
        id: 'submit' + new Date().toISOString(),
        type: 'danger',
        text: `Não foi possivel salvar as configurações`,
      });
      setLoading(false);
    }
  };

  return (
    <Box
      marginX="auto"
      maxWidth={{
        xs: '100%',
        md: '60%',
      }}
    >
      <Page>
        <Page.Header title="Configurações" />
        <Page.Body>
          <Layout columns="1">
            <Layout.Section>
              <Tabs preSelectedTab={0} fullWidth={true}>
                <Tabs.Item label="Integração">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Tokens CRM Bônus" />
                      <Card.Body>
                        <Box display="grid" gap="6">
                          <FormField label="Código de API">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="crm_bonus_cod"
                              name="crm_bonus_cod"
                              onChange={handleChangeConfig}
                              value={config?.crm_bonus_cod}
                              placeholder={
                                'Informe o Código fornecido pela CRM Bônus'
                              }
                            />
                          </FormField>
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                </Tabs.Item>
                <Tabs.Item label="Regras de Exibição">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Popup no carrinho" />
                      <Card.Body>
                        <Box display="grid" gap="4">
                          <Text>
                            Ative esta opção para adicionar o popup de resgate
                            de bônus ao carrinho.
                          </Text>
                          <Checkbox
                            disabled={loadingGetConfig || loading}
                            name="popup_in_cart_show"
                            label="Exibir popup no carrinho de compras?"
                            checked={config?.popup_in_cart_show}
                            onChange={handleChangeConfig}
                          />
                        </Box>
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Header title="Barra de progresso" />
                      <Card.Body>
                        <Box display="grid" gap="4">
                          <Text>
                            Ative esta opção para que uma barra de progresso
                            seja exibida, indicando o quanto ainda precisa ser
                            adicionado ao carrinho para que o cliente possa
                            resgatar o total de bônus disponíveis.
                          </Text>
                          <Checkbox
                            disabled={loadingGetConfig || loading}
                            name="popup_progress_bar_show"
                            label="Exibir barra de prograsso no popup?"
                            checked={config?.popup_progress_bar_show}
                            onChange={handleChangeConfig}
                          />
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                </Tabs.Item>
                <Tabs.Item label="Regras de Bloqueios">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Produtos com desconto" />
                      <Card.Body>
                        <Box display="grid" gap="4">
                          <Text>
                            Marque esta opção para impedir a venda de produtos
                            que já possuem descontos aplicados diretamente, como
                            preços promocionais (exibidos como &quot;de /
                            por&quot;) ou ofertas similares.
                          </Text>
                          <Checkbox
                            disabled={loadingGetConfig || loading}
                            name="block_products_with_discount"
                            label="Bloquear produtos com descontos aplicados"
                            checked={config?.block_products_with_discount}
                            onChange={handleChangeConfig}
                          />
                        </Box>
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Header title="Cupons de desconto" />
                      <Card.Body>
                        <Box display="grid" gap="4">
                          <Text>
                            Marque esta opção para impedir o uso do giftback
                            (bônus) quando o cliente estiver utilizando algum
                            cupom de desconto no carrinho.
                          </Text>
                          <Checkbox
                            disabled={loadingGetConfig || loading}
                            name="block_bonus_with_coupon"
                            label="Bloquear giftback quando houver cupom aplicado"
                            checked={config?.block_bonus_with_coupon}
                            onChange={handleChangeConfig}
                          />
                        </Box>
                      </Card.Body>
                    </Card>

                    {config?.black_list_products &&
                    Object.entries(config.black_list_products).length ? (
                      <Box display="grid" gap="2">
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="4"
                        >
                          <Title as="h3">
                            {Object.entries(config.black_list_products).length}{' '}
                            {Object.entries(config.black_list_products)
                              .length === 1
                              ? `Produto bloqueado`
                              : `Produtos bloqueados`}
                          </Title>
                          <Button
                            appearance="neutral"
                            onClick={handleOpenSidebarProduct}
                          >
                            <Icon
                              color="currentColor"
                              source={<PlusCircleIcon />}
                            />
                            Selecionar produtos
                          </Button>
                        </Box>
                        <Table>
                          <Table.Head>
                            <Table.Row>
                              <Table.Cell width="100px" as="th">
                                #ID
                              </Table.Cell>
                              <Table.Cell as="th">Nome</Table.Cell>
                              <Table.Cell width="40px" as="th">
                                Ações
                              </Table.Cell>
                            </Table.Row>
                          </Table.Head>
                          <Table.Body>
                            {Object.entries(config.black_list_products).map(
                              ([id, name]) => (
                                <Table.Row key={id}>
                                  <Table.Cell>#{id}</Table.Cell>
                                  <Table.Cell>{name}</Table.Cell>
                                  <Table.Cell>
                                    <Box display="flex" justifyContent="center">
                                      <IconButton
                                        as="button"
                                        type="button"
                                        onClick={() =>
                                          handleRemoveProductForBlackList(
                                            parseInt(`${id}`),
                                          )
                                        }
                                        size="30px"
                                        source={<TrashIcon size="small" />}
                                      />
                                    </Box>
                                  </Table.Cell>
                                </Table.Row>
                              ),
                            )}
                          </Table.Body>
                        </Table>
                      </Box>
                    ) : (
                      <Card>
                        <Card.Body>
                          <EmptyMessage
                            title="Produtos bloqueados"
                            text="Nenhum produto esta sendo bloqueado"
                            icon={<ForbiddenIcon size={32} />}
                            actions={
                              <Link
                                as="button"
                                type="button"
                                appearance="primary"
                                onClick={handleOpenSidebarProduct}
                              >
                                <Icon
                                  color="currentColor"
                                  source={<PlusCircleIcon />}
                                />
                                Selecionar produtos
                              </Link>
                            }
                          />
                        </Card.Body>
                      </Card>
                    )}

                    {config?.black_list_category &&
                    Object.entries(config.black_list_category).length ? (
                      <Box display="grid" gap="2">
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap="4"
                        >
                          <Title as="h3">
                            {Object.entries(config.black_list_category).length}{' '}
                            {Object.entries(config.black_list_category)
                              .length === 1
                              ? `Categoria bloqueada`
                              : `Categorias bloqueadas`}
                          </Title>
                          <Button
                            appearance="neutral"
                            onClick={handleOpenSidebarCategory}
                          >
                            <Icon
                              color="currentColor"
                              source={<PlusCircleIcon />}
                            />
                            Selecionar categorias
                          </Button>
                        </Box>
                        <Table>
                          <Table.Head>
                            <Table.Row>
                              <Table.Cell width="100px" as="th">
                                #ID
                              </Table.Cell>
                              <Table.Cell as="th">Nome</Table.Cell>
                              <Table.Cell width="40px" as="th">
                                Ações
                              </Table.Cell>
                            </Table.Row>
                          </Table.Head>
                          <Table.Body>
                            {Object.entries(config.black_list_category).map(
                              ([id, name]) => (
                                <Table.Row key={id}>
                                  <Table.Cell>#{id}</Table.Cell>
                                  <Table.Cell>{name}</Table.Cell>
                                  <Table.Cell>
                                    <Box display="flex" justifyContent="center">
                                      <IconButton
                                        as="button"
                                        type="button"
                                        onClick={() =>
                                          handleRemoveCategoryForBlackList(
                                            parseInt(`${id}`),
                                          )
                                        }
                                        size="30px"
                                        source={<TrashIcon size="small" />}
                                      />
                                    </Box>
                                  </Table.Cell>
                                </Table.Row>
                              ),
                            )}
                          </Table.Body>
                        </Table>
                      </Box>
                    ) : (
                      <Card>
                        <Card.Body>
                          <EmptyMessage
                            title="Categorias bloqueadas"
                            text="Nenhuma categoria esta sendo bloqueada"
                            icon={<ForbiddenIcon size={32} />}
                            actions={
                              <Link
                                appearance="primary"
                                as="button"
                                type="button"
                                onClick={handleOpenSidebarCategory}
                              >
                                <Icon
                                  color="currentColor"
                                  source={<PlusCircleIcon />}
                                />
                                Selecionar categorias
                              </Link>
                            }
                          />
                        </Card.Body>
                      </Card>
                    )}
                  </Box>
                </Tabs.Item>
                <Tabs.Item label="Textos">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Textos" />
                      <Card.Body>
                        <Box display="grid" gap="6">
                          <FormField label="Titulo do popup">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_title"
                              name="popup_title"
                              onChange={handleChangeConfig}
                              value={config?.popup_title}
                              placeholder={'Ex.: Giftback'}
                            />
                          </FormField>
                          <Title as="h4">Cliente anonimo</Title>
                          <FormField label="Mensagem para clientes não logados">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_not_logged"
                              name="popup_text_not_logged"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_not_logged}
                              placeholder={
                                'Ex.: Você precisa estar logado para verificar o seu bônus'
                              }
                            />
                          </FormField>
                          <FormField label="Texto do botão para clientes não logados">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_not_logged_cta"
                              name="popup_text_not_logged_cta"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_not_logged_cta}
                              placeholder={'Ex.: Fazer login'}
                            />
                          </FormField>
                          <Title as="h4">Cliente sem telefone</Title>
                          <FormField label="Mensagem quando cliente não tiver telefone cadastrado">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_not_phone"
                              name="popup_text_not_phone"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_not_phone}
                              placeholder={
                                'Ex.: Você precisa ter um telefone cadastrado para usar seu bônus'
                              }
                            />
                          </FormField>
                          <FormField label="Texto do botão adicionar telefone">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_not_phone_cta"
                              name="popup_text_not_phone_cta"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_not_phone_cta}
                              placeholder={'Ex.: Adicionar um telefone'}
                            />
                          </FormField>
                          <Title as="h4">Bônus</Title>
                          <FormField label="Mensagem quando cliente não tiver bônus para ser resgatado">
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_not_bonus"
                              name="popup_text_not_bonus"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_not_bonus}
                              placeholder={
                                'Ex.: Você não possui bônus nessa compra.'
                              }
                            />
                          </FormField>
                          <FormField
                            label="Mensagem quando cliente tiver bônus para ser resgatado"
                            helpText="{bonus} será substituido pelo valor do bônus"
                            showHelpText={true}
                          >
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_text_have_bonus"
                              name="popup_text_have_bonus"
                              onChange={handleChangeConfig}
                              value={config?.popup_text_have_bonus}
                              placeholder={
                                'Ex.: Você pode resgatar <b>{bonus}</b> para esta compra'
                              }
                            />
                          </FormField>
                          <FormField
                            label="Mensagem da barra de progresso"
                            helpText="{valor} será substituido pelo valor restante para atingir o total dos bônus e {total} será substituido pelo valor total em compras que o cliente precisa para resgatar todos os bônus"
                            showHelpText={true}
                          >
                            <Input
                              disabled={loadingGetConfig || loading}
                              id="popup_progress_bar_text"
                              name="popup_progress_bar_text"
                              onChange={handleChangeConfig}
                              value={config?.popup_progress_bar_text}
                              placeholder={
                                'Ex.: Restam {valor} para resgatar o total de {total} em bonus'
                              }
                            />
                          </FormField>
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                </Tabs.Item>
                <Tabs.Item label="Cores">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Textos" />
                      <Card.Body>
                        <Box display="flex" flexWrap="wrap">
                          <Box
                            width={{
                              xs: '100%',
                              md: '50%',
                            }}
                            padding="4"
                          >
                            <InputColor
                              label="Cor de fundo"
                              name="popup_bg_color"
                              onChange={handleChangeConfig}
                              required={true}
                              disabled={loading || !config}
                              value={config?.popup_bg_color || '#ffffff'}
                            />
                          </Box>
                          <Box
                            width={{
                              xs: '100%',
                              md: '50%',
                            }}
                            padding="4"
                          >
                            <InputColor
                              label="Cor dos titulos"
                              name="popup_title_color"
                              onChange={handleChangeConfig}
                              required={true}
                              disabled={loading || !config}
                              value={config?.popup_title_color || '#000000'}
                            />
                          </Box>
                          <Box
                            width={{
                              xs: '100%',
                              md: '50%',
                            }}
                            padding="4"
                          >
                            <InputColor
                              label="Cor dos textos"
                              name="popup_text_color"
                              onChange={handleChangeConfig}
                              required={true}
                              disabled={loading || !config}
                              value={config?.popup_text_color || '#000000'}
                            />
                          </Box>
                          <Box
                            width={{
                              xs: '100%',
                              md: '50%',
                            }}
                            padding="4"
                          >
                            <InputColor
                              label="Cor principal"
                              name="popup_primary_color"
                              onChange={handleChangeConfig}
                              required={true}
                              disabled={loading || !config}
                              value={config?.popup_primary_color || '#007BFF'}
                            />
                          </Box>
                          <Box
                            width={{
                              xs: '100%',
                              md: '50%',
                            }}
                            padding="4"
                          >
                            <InputColor
                              label="Cor de sucesso"
                              name="popup_success_color"
                              onChange={handleChangeConfig}
                              required={true}
                              disabled={loading || !config}
                              value={config?.popup_success_color || '#28A745'}
                            />
                          </Box>
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                </Tabs.Item>
                <Tabs.Item label="Customizações">
                  <Box display="grid" gap="6">
                    <Card>
                      <Card.Header title="Reenvio de PIN via WhatsApp" />
                      <Card.Body>
                        <Box display="grid" gap="4">
                          <Text>  
                            Ative esta funcionalidade para permitir que os clientes
                            façam o reenvio de PIN via WhatsApp.
                          </Text>
                          <Checkbox
                            disabled={loadingGetConfig || loading}
                            name="allow_resend_whatsapp_pin"
                            label="Ativar reenvio de PIN via WhatsApp"
                            checked={config?.allow_resend_whatsapp_pin || false}
                            onChange={handleChangeConfig}
                          />
                        </Box>
                      </Card.Body>
                    </Card>
                  </Box>
                </Tabs.Item>
              </Tabs>
              <Box display="flex" marginY="2" justifyContent="flex-end">
                <Button
                  disabled={loading}
                  appearance="primary"
                  onClick={handleSubmit}
                >
                  {loading ? <Spinner color="currentColor" size="small" /> : ''}
                  Salvar configurações
                </Button>
              </Box>
            </Layout.Section>
          </Layout>
        </Page.Body>
      </Page>

      {/* SIDEBAR BUSCA PRODUTOS */}
      <Sidebar
        padding="base"
        open={openSearchProdut}
        onRemove={() => setOpenSearchProdut(false)}
      >
        <Sidebar.Header>
          <Box display="grid" gap="2">
            <Title as="h4">Selecione os produtos</Title>
            <Input.Search
              placeholder="Busque os produtos"
              onChange={handleChangeProductQuerySearch}
              value={searchProductQuery}
            />
          </Box>
        </Sidebar.Header>
        <Sidebar.Body>
          <Box display="grid" gap="4">
            {loadingProducts ? (
              <InteractiveList>
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
              </InteractiveList>
            ) : products.length ? (
              <>
                <InteractiveList>
                  {products.map((product: any, index: number) => (
                    <InteractiveList.CheckboxItem
                      key={index}
                      title={''}
                      showTitle={false}
                      checkbox={{
                        name: 'checkbox-element-' + index,
                        checked:
                          config &&
                          config.black_list_products &&
                          product.id in config.black_list_products
                            ? true
                            : false,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onChange: (event: ChangeEvent<HTMLInputElement>) =>
                          handleAddProductToBlackList(event, product),
                      }}
                    >
                      <Box display="flex" alignItems="center" gap="3">
                        {product.images.length ? (
                          <Thumbnail
                            src={product.images[0].src}
                            alt={product.name[defaultLang]}
                            width="40px"
                          />
                        ) : (
                          <Thumbnail
                            alt={product.name[defaultLang]}
                            width="40px"
                          />
                        )}
                        <Text>{product.name[defaultLang]}</Text>
                      </Box>
                    </InteractiveList.CheckboxItem>
                  ))}
                </InteractiveList>
                {productsMeta && productsMeta.pages > 1 ? (
                  <Pagination
                    activePage={productsMeta.page}
                    onPageChange={(page) => searchProducts(page)}
                    pageCount={productsMeta.pages}
                  />
                ) : (
                  <></>
                )}
              </>
            ) : (
              <EmptyMessage
                title="Produtos"
                text="Nenhum produto encontrado"
                icon={<SearchIcon size={32} />}
                actions={
                  <Button
                    appearance="primary"
                    onClick={() => {
                      setSearchProductQuery('');
                      searchProducts(1, true);
                    }}
                  >
                    <Icon color="currentColor" source={<UndoIcon />} />
                    Limpar busca
                  </Button>
                }
              />
            )}
          </Box>
        </Sidebar.Body>
      </Sidebar>

      {/* SIDEBAR BUSCA CATEGORIAS */}
      <Sidebar
        padding="base"
        open={openSearchCategory}
        onRemove={() => setOpenSearchCategory(false)}
      >
        <Sidebar.Header>
          <Box display="grid" gap="2">
            <Title as="h4">Selecione as categorias</Title>
          </Box>
        </Sidebar.Header>
        <Sidebar.Body>
          <Box display="grid" gap="4">
            {loadingCategories ? (
              <InteractiveList>
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
                <InteractiveList.CheckboxItemSkeleton title="" />
              </InteractiveList>
            ) : categories.length ? (
              <>
                <InteractiveList>
                  {categories.map((category: any, index: number) => (
                    <InteractiveList.CheckboxItem
                      key={index}
                      title={category.name[defaultLang]}
                      showTitle={true}
                      checkbox={{
                        name: 'checkbox-element-' + index,
                        checked:
                          config &&
                          config.black_list_category &&
                          category.id in config.black_list_category
                            ? true
                            : false,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onChange: (event: ChangeEvent<HTMLInputElement>) =>
                          handleAddCategoryToBlackList(event, category),
                      }}
                    />
                  ))}
                </InteractiveList>
                {categoryMeta && categoryMeta.pages > 1 ? (
                  <Pagination
                    activePage={categoryMeta.page}
                    onPageChange={(page) => searchCategories(page)}
                    pageCount={categoryMeta.pages}
                  />
                ) : (
                  <></>
                )}
              </>
            ) : (
              <EmptyMessage
                title="Categorias"
                text="Nenhuma categoria encontrada"
                icon={<SearchIcon size={32} />}
              />
            )}
          </Box>
        </Sidebar.Body>
      </Sidebar>
    </Box>
  );
};

export default ConfigsPage;
